import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import type {AsyncApolloQueryResult} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {CourseProgress} from "#content-library-legacy/modules/courses-dashboard-types.ts";
import {getValidDomainSlug} from "#content-library-legacy/styles/shared.ts";

import type {
    courseProgresses,
    courseProgressesVariables,
    courseProgresses_user_courseProgresses as GqlCourseProgress,
} from "./__graphql-types__/courseProgresses.ts";

type RecentTopicGraphQL = {
    slug: string;
    title: string;
    unit: {
        title: string;
        thumbnailUrl: string;
        domain: string;
        nodeUrl: string;
    };
};

type ClassPickerQueryData = {
    classPicker?: {
        enabled: boolean;
        classesInGradeLevels: Array<{
            id: string;
            subjects: Array<{
                id: string;
            }>;
        }>;
        gradeLevelGroups: Array<{
            title: string;
            levels: Array<{
                id: string;
                title: string;
            }>;
        }>;
        domains: Array<{
            slug: string;
            title: string;
            subjects: Array<{
                id: string;
                key: string;
                slug: string;
                title: string;
            }>;
        }>;
    };
    user?: {
        id: string;
        selfSelectedGradeLevel: string | null;
        districtGradeLevel: string | null;
        cleverId: string | null;
        isDistrictSynced: boolean;
        classPickerSubjectProgresses: Array<{
            topic: {
                domainSlug: string;
                iconPath: string;
                id: string;
                slug: string;
                title: string;
                relativeUrl: string;
            };
            unitProgresses: Array<{
                lastWorkedOn: string;
                currentMastery: {
                    percentage: number;
                };
                topic: {
                    id: string;
                    iconPath: string;
                    title: string;
                    relativeUrl: string;
                    masteryEnabled: boolean;
                    subjectMasteryEnabled: boolean;
                };
            }>;
        }>;
        recentTopics: Array<RecentTopicGraphQL>;
    };
};

export type ClassPickerQuery = AsyncApolloQueryResult<ClassPickerQueryData>;

type GradeLevelsToClassesMap = Record<string, string>;
export type CourseDashboardProps = {
    domain: string;
    isFirstTime: boolean;
    hidePickerIfEmpty: boolean;
    districtGradeLevel: string | null | undefined;
    classPickerData: {
        enabled: boolean;
        classesInGradeLevels: GradeLevelsToClassesMap;
        domains: Array<{
            slug: string;
            subjects: Array<{
                id: string;
                key: string;
                slug: string;
                title: string;
            }>;
            title: string;
        }>;
        gradeLevelGroups: Array<{
            title: string;
            levels: Array<{
                id: string;
                title: string;
            }>;
        }>;
    };
    userCourses: Array<{
        domain: string;
        icon: string;
        id: string;
        slug: string;
        title: string;
        url: string;
        topicList: Array<{
            icon: string;
            lastWorkedOn: string;
            masteryPercentage: number;
            masteryEnabled: boolean;
            subjectMasteryEnabled: boolean;
            title: string;
            url: string;
        }>;
    }>;
};

// RecentTopic is the data that the ResumeLearning widget needs. Transformed from GraphQL
export type RecentTopic = {
    kind: string;
    title: string;
    thumbnailUrl: string;
    domain: string;
    nodeUrl: string;
    breadcrumbs: Array<{
        href: string;
        title: string;
    }>;
};

export type ResumeLearningProps = {
    showPlaceholder: boolean;
    title: string;
    domain: string;
    referrer: string;
    contentItems: Array<RecentTopic>;
};

const COURSE_PROGRESS_FRAGMENT = gql`
    fragment CourseProgress on SubjectProgress {
        topic {
            domainSlug
            iconPath
            id
            slug
            title: translatedTitle
            relativeUrl
        }

        unitProgresses {
            lastWorkedOn
            currentMastery {
                percentage
            }
            topic {
                id
                iconPath
                title: translatedTitle
                relativeUrl
                masteryEnabled
                subjectMasteryEnabled
            }
        }
    }
`;

export const CLASS_PICKER_QUERY: GqlOperation<ClassPickerQueryData> = gqlOp(gql`
    query MyCourses {
        classPicker {
            enabled
            classesInGradeLevels {
                id
                subjects {
                    id
                }
            }
            gradeLevelGroups {
                title
                levels {
                    id
                    title
                }
            }
            domains {
                slug
                title
                subjects {
                    id
                    key
                    slug
                    title: translatedTitle
                }
            }
        }

        user {
            id
            selfSelectedGradeLevel
            districtGradeLevel
            cleverId
            isDistrictSynced
            classPickerSubjectProgresses {
                ...CourseProgress
            }
            recentTopics(limit: 4) {
                id
                slug
                title: translatedTitle
                unit: parent {
                    id
                    title: translatedTitle
                    thumbnailUrl: largeIconPath
                    domain: domainSlug
                    nodeUrl: relativeUrl
                }
            }
        }
    }

    ${COURSE_PROGRESS_FRAGMENT}
`);

export const COURSE_PROGRESSES_QUERY: GqlOperation<
    courseProgresses,
    courseProgressesVariables
> = gqlOp(gql`
    query courseProgresses($courseIds: [String!]!) {
        user {
            id
            courseProgresses(courseIds: $courseIds) {
                ...CourseProgress
            }
        }
    }
    ${COURSE_PROGRESS_FRAGMENT}
`);

export const mapGqlCourseProgress = (
    courseProgress: GqlCourseProgress | null | undefined,
): CourseProgress | null => {
    const course = courseProgress?.topic;
    if (!courseProgress || !course) {
        return null;
    }
    return {
        id: course.id,
        slug: course.slug ?? "",
        title: course.title ?? "",
        domain: getValidDomainSlug(course.domainSlug ?? null),
        icon: course.iconPath,
        url: course.relativeUrl ?? "",
        topicList:
            courseProgress.unitProgresses
                ?.filter(isTruthy)
                .map((unitProgress) => ({
                    lastWorkedOn: unitProgress.lastWorkedOn,
                    title: unitProgress.topic?.title ?? "",
                    url: unitProgress.topic?.relativeUrl ?? "",
                    icon: unitProgress.topic?.iconPath,
                    masteryPercentage:
                        unitProgress.currentMastery?.percentage ?? 0,
                    masteryEnabled: !!unitProgress.topic?.masteryEnabled,
                    subjectMasteryEnabled:
                        !!unitProgress.topic?.subjectMasteryEnabled,
                })) ?? [],
    };
};

export const mapQueryClassPickerToProps = (
    classPickerQuery: ClassPickerQuery,
): CourseDashboardProps => {
    const domain = "default";

    if (classPickerQuery.loading || !classPickerQuery.data) {
        return {
            domain,
            isFirstTime: false,
            hidePickerIfEmpty: false,
            districtGradeLevel: null,
            classPickerData: {
                enabled: true,
                classesInGradeLevels: {},
                domains: [],
                gradeLevelGroups: [],
            },
            userCourses: [],
        };
    }

    const {classPicker, user} = classPickerQuery.data;

    const enabled = !!(classPicker && classPicker.enabled);

    const classesInGradeLevels: GradeLevelsToClassesMap = classPicker
        ? classPicker.classesInGradeLevels.reduce<Record<string, any>>(
              (acc: GradeLevelsToClassesMap, gradeLevel) => ({
                  ...acc,
                  // We need to apply a filter since it's possible these
                  // courses come back null from the server.
                  [gradeLevel.id]: gradeLevel.subjects
                      .filter(isTruthy)
                      .map((s) => s.id),
              }),
              {},
          )
        : {};

    const domains = classPicker
        ? classPicker.domains.map((domain) => ({
              slug: domain.slug,
              title: domain.title,
              subjects: domain.subjects.map((subject) => ({
                  id: subject.id,
                  key: subject.key,
                  slug: subject.slug,
                  title: subject.title,
              })),
          }))
        : [];

    const gradeLevelGroups = classPicker ? classPicker.gradeLevelGroups : [];

    const userProgresses = user ? user.classPickerSubjectProgresses : [];

    const userCourses = userProgresses.map((progress) => ({
        domain: progress.topic.domainSlug,
        icon: progress.topic.iconPath,
        id: progress.topic.id,
        slug: progress.topic.slug,
        title: progress.topic.title,
        url: progress.topic.relativeUrl,

        topicList: progress.unitProgresses.map((unitProgress) => ({
            icon: unitProgress.topic.iconPath,
            lastWorkedOn: unitProgress.lastWorkedOn,
            masteryEnabled: unitProgress.topic.masteryEnabled,
            masteryPercentage: unitProgress.currentMastery.percentage,
            subjectMasteryEnabled: unitProgress.topic.subjectMasteryEnabled,
            title: unitProgress.topic.title,
            url: unitProgress.topic.relativeUrl,
        })),
    }));

    return {
        domain,
        districtGradeLevel: user && user.districtGradeLevel,
        isFirstTime: userCourses.length === 0,
        // hidePickerIfEmpty if the user is a Clever Library user
        // this will also be true for our legacy clever users,
        // but since we're deprecating that experience we should
        // no longer be seeing new students from the legacy app.
        hidePickerIfEmpty: user
            ? user.cleverId !== null && !user.isDistrictSynced
            : false,
        classPickerData: {
            enabled,
            classesInGradeLevels,
            domains,
            gradeLevelGroups,
        },
        userCourses,
    };
};

const transformRecentTopic = (
    gqlRecentTopic: RecentTopicGraphQL,
): RecentTopic | null | undefined => {
    const unit = gqlRecentTopic.unit;
    if (!unit) {
        return null;
    }

    return {
        kind: "concept",
        title: gqlRecentTopic.title,
        thumbnailUrl: unit.thumbnailUrl,
        domain: unit.domain,
        nodeUrl: `${unit.nodeUrl}#${gqlRecentTopic.slug}`,
        breadcrumbs: [
            {
                href: unit.nodeUrl,
                title: unit.title,
            },
        ],
    };
};

const transformRecentTopics = (
    gqlRecentTopics: Array<RecentTopicGraphQL>,
): Array<RecentTopic> => {
    if (!gqlRecentTopics) {
        return [];
    }

    // Map the recent topic and filter empties.
    return gqlRecentTopics.map(transformRecentTopic).filter(isTruthy);
};

export const mapQueryToResumeLearningProps = (
    classPickerQuery: ClassPickerQuery,
): ResumeLearningProps => {
    const domain = "default";
    const referrer = "resume_learning";
    const showPlaceholder = true;
    const title = t`Resume learning`;

    const defaults = {
        domain,
        referrer,
        showPlaceholder,
        title,
        contentItems: [],
    } as const;

    if (classPickerQuery.loading || !classPickerQuery.data) {
        // @ts-expect-error - TS2322 - Type '{ readonly domain: "default"; readonly referrer: "resume_learning"; readonly showPlaceholder: true; readonly title: string; readonly contentItems: readonly []; }' is not assignable to type 'ResumeLearningProps'.
        return defaults;
    }

    const {user} = classPickerQuery.data;

    const contentItems = user ? transformRecentTopics(user.recentTopics) : [];

    return {
        ...defaults,
        contentItems,
    };
};

export const getGradeLevel = (classPickerQuery: ClassPickerQuery): string => {
    if (
        classPickerQuery.loading ||
        !classPickerQuery.data ||
        !classPickerQuery.data.user
    ) {
        return "";
    }

    const {user} = classPickerQuery.data;

    return user.districtGradeLevel || user.selfSelectedGradeLevel || "";
};

export const shouldSkipGradePicker = (
    classPickerQuery: ClassPickerQuery,
): boolean => {
    if (
        classPickerQuery.loading ||
        !classPickerQuery.data ||
        !classPickerQuery.data.user
    ) {
        return false;
    }

    const {user} = classPickerQuery.data;

    return Boolean(user.districtGradeLevel);
};
